import { serializeItemData, deserializeItemData, serializeFilters } from '@animaapp/playground-backend-shared-types';
import { AnimaAPI } from './api';
import { raiseIfResponseError } from './errors';
import { resolveEntityName } from './utils/entityName';

export class AnimaEntityDB {
  constructor(
    private readonly entityName: string,
    private readonly animaAPI: AnimaAPI
  ) {}

  async create(data: any): Promise<any> {
    const serializedData = serializeItemData(data);

    const response = await this.animaAPI.fetchAPI(`/items/${this.entityName}`, {
      method: 'POST',
      body: JSON.stringify(serializedData),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    await raiseIfResponseError(response);

    const item = await response.json();
    return mapItemToObject(item);
  }

  async findById(id: string): Promise<any | null> {
    const response = await this.animaAPI.fetchAPI(`/items/${this.entityName}/${id}`, {
      method: 'GET',
    });

    if (response.status === 404) {
      return null;
    }
    await raiseIfResponseError(response);

    const json = await response.json();
    return mapItemToObject(json);
  }

  async findMany(params?: { where?: any; orderBy?: any; limit?: number; offset?: number }): Promise<any[]> {
    const filters = {
      where: params?.where,
      orderBy: params?.orderBy,
      limit: params?.limit,
      offset: params?.offset,
    };
    const serializedFilters = JSON.stringify(serializeFilters(filters));

    const urlParams = new URLSearchParams();
    urlParams.set('filters', serializedFilters);

    const response = await this.animaAPI.fetchAPI(`/items/${this.entityName}?${urlParams.toString()}`, {
      method: 'GET',
    });

    await raiseIfResponseError(response);

    const items = await response.json();
    return items.map(mapItemToObject);
  }

  async updateById(id: string, data: any): Promise<any> {
    const serializedData = serializeItemData(data);

    const response = await this.animaAPI.fetchAPI(`/items/${this.entityName}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(serializedData),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    await raiseIfResponseError(response);

    const item = await response.json();
    return mapItemToObject(item);
  }

  async deleteById(id: string): Promise<any> {
    const response = await this.animaAPI.fetchAPI(`/items/${this.entityName}/${id}`, {
      method: 'DELETE',
    });

    await raiseIfResponseError(response);

    const item = await response.json();
    return mapItemToObject(item);
  }
}

function mapItemToObject(item: any): any {
  const createdAtDate = new Date(item.createdAt * 1000);
  const updatedAtDate = new Date(item.updatedAt * 1000);
  const createdByUserId = item.createdByUserId ?? null;

  const deserializedData = deserializeItemData(item.data);

  return {
    ...deserializedData,
    id: item.id,
    createdAt: createdAtDate,
    updatedAt: updatedAtDate,
    createdByUserId,
  };
}

export type AnimaDBParams = {
  animaAPI: AnimaAPI;
};

export type AnimaDB = Record<string, AnimaEntityDB>;

export function createAnimaDB(params: AnimaDBParams): AnimaDB {
  return new Proxy(
    {},
    {
      get: (_, prop) => {
        if (typeof prop !== 'string') {
          throw new Error('Entity name must be a string');
        }

        if (prop.trim() === '') {
          throw new Error('Entity name cannot be empty');
        }

        const entityName = resolveEntityName(prop);
        return new AnimaEntityDB(entityName, params.animaAPI);
      },
    }
  );
}
